import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ChallengeContainer } from '@/features/challenge/ChallengeContainer';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import bugHunt1Data from '@/data/challenges/bug-hunt-1.json';
import documentAnalysis1Data from '@/data/challenges/document-analysis-1.json';

export default function ChallengePage() {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const [challengeData, setChallengeData] = useState<any>(null);

  const challenges = [
    {
      id: 'bug-hunt-1',
      type: 'bug-hunt',
      title: 'The Missing Return',
      difficulty: 'easy',
      timeLimit: '5 min',
      xp: 75,
      unlocked: true,
    },
    {
      id: 'document-analysis-1',
      type: 'document-analysis',
      title: 'Budget Report Analysis',
      difficulty: 'easy',
      timeLimit: '10 min',
      xp: 100,
      unlocked: true,
    },
    {
      id: 'bug-hunt-2',
      type: 'bug-hunt',
      title: 'Loop Error',
      difficulty: 'easy',
      timeLimit: '5 min',
      xp: 75,
      unlocked: false,
    },
    {
      id: 'feature-sprint-1',
      type: 'feature-sprint',
      title: 'Add User Validation',
      difficulty: 'medium',
      timeLimit: '10 min',
      xp: 150,
      unlocked: false,
    },
  ];

  useEffect(() => {
    if (challengeId === 'bug-hunt-1' || challengeId === '1') {
      setChallengeData(bugHunt1Data);
    } else if (challengeId === 'document-analysis-1') {
      setChallengeData(documentAnalysis1Data);
    }
  }, [challengeId]);

  // Challenge selection screen
  if (!challengeId) {
    return (
      <div className="min-h-screen p-8 bg-black">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="mb-4"
            >
              ← Back to Home
            </Button>
            <h1 className="text-4xl font-bold mb-2 text-primary-400">
              Challenge Mode
            </h1>
            <p className="text-primary-200">
              Test your skills with timed coding challenges
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {challenges.map((challenge) => (
              <Card
                key={challenge.id}
                variant={challenge.unlocked ? 'elevated' : 'default'}
                className={
                  challenge.unlocked
                    ? 'hover:border-primary-500/50 cursor-pointer transition-all duration-300 hover:scale-105'
                    : 'opacity-50 cursor-not-allowed'
                }
                onClick={() =>
                  challenge.unlocked && navigate(`/challenge/${challenge.id}`)
                }
              >
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <Badge
                      variant={
                        challenge.difficulty === 'easy'
                          ? 'success'
                          : challenge.difficulty === 'medium'
                          ? 'warning'
                          : 'error'
                      }
                      size="sm"
                    >
                      {challenge.difficulty}
                    </Badge>
                    {!challenge.unlocked && <span className="text-xl">🔒</span>}
                  </div>
                  <Badge variant="info" size="sm" className="mb-2">
                    {challenge.type}
                  </Badge>
                  <CardTitle className="text-lg">{challenge.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-3 text-sm text-primary-300">
                    <span>⏱️ {challenge.timeLimit}</span>
                    <span>✨ {challenge.xp} XP</span>
                  </div>
                  {challenge.unlocked ? (
                    <Button variant="primary" size="sm" className="w-full">
                      Start Challenge
                    </Button>
                  ) : (
                    <p className="text-sm text-primary-200/50">
                      Complete previous challenges to unlock
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (!challengeData) {
    return (
      <div className="min-h-screen p-8 bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-primary-200">Loading challenge...</p>
        </div>
      </div>
    );
  }

  // Challenge content
  return (
    <div className="min-h-screen p-8 bg-black">
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/challenge')}
        >
          ← Back to Challenges
        </Button>
      </div>
      <ChallengeContainer challengeData={challengeData} />
    </div>
  );
}
